import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useThemeStore } from "@/stores/theme";
import { useAuthStore } from "@/stores/auth";
import type { UserRole } from "@/stores/auth";
import { Loader2 } from "lucide-react";
import { BrandMark } from "@/components/brand/BrandMark";
import { ToastProvider } from "@/components/ui/toast";
import { queryClient } from "@/lib/queryClient";
import { hasValidTokens } from "@/lib/api";
import { isDemoSession } from "@/lib/demoMode";
import {
  beginProviderSessionRestore,
  isProviderAuthEnabled,
  isProviderAuthRequested,
  isProviderSessionRestorePending,
  revokeActiveProviderSessionAfterPeerLogout,
} from "@/lib/providerSession";
import {
  isProviderLogoutEpochStorageKey,
  providerSessionRestoreAction,
} from "@/lib/providerSessionRestore";

import { DEFAULT_ADMIN_PATH } from "@/components/admin/navigation";
import { supportRouteSource } from "@/routes/supportPaths";
import { AccountDeletionRequestsPage } from "@/pages/admin/AccountDeletionRequestsPage";
import {
  loadAdminAppealDetailPage,
  loadAdminAppealsPage,
  loadAdminBillingPage,
  loadAdminBroadcastPage,
  loadAdminDirectorsPage,
  loadAdminDirectDeployPage,
  loadAdminGroupsPage,
  loadAdminInvitesPage,
  loadAdminManagersPage,
  loadAdminPendingPage,
  loadAdminPremiumApprovalPage,
  loadAdminPremiumProductionPage,
  loadAdminSupervisorsPage,
  loadAdminUsersKanbanPage,
  loadAdminUsersPage,
  loadBannedPage,
  loadClientsPage,
  loadDashboardPage,
  loadFeedbackDetailPage,
  loadFeedbackPage,
  loadForgotPasswordPage,
  loadInviteRedirectPage,
  loadInviteRegisterPage,
  loadLeadsEscalationsPage,
  loadLeadsPage,
  loadLegacySupportTicketsPage,
  loadLegacyTicketDetailPage,
  loadLoginPage,
  loadNewRequestPage,
  loadNotificationsPage,
  loadPaymentsPage,
  loadPendingPage,
  loadProfilePage,
  loadRegisterPage,
  loadRejectedPage,
  loadRequestDetailPage,
  loadRequestsPage,
  loadSiteDeletionRequestsPage,
  loadSiteEditsPage,
  loadSiteEditorPage,
  loadSiteRevisionsIndexPage,
  loadSitesPage,
  loadWenlixAnalyticsPage,
} from "@/routes/preloaders";

// queryClient imported from @/lib/queryClient (shared with api.ts interceptors)

const LoginPage = lazy(() => loadLoginPage().then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => loadRegisterPage().then((module) => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() => loadForgotPasswordPage().then((module) => ({ default: module.ForgotPasswordPage })));
const InviteRegisterPage = lazy(() => loadInviteRegisterPage().then((module) => ({ default: module.InviteRegisterPage })));
const InviteRedirectPage = lazy(() => loadInviteRedirectPage().then((module) => ({ default: module.InviteRedirectPage })));
const BannedPage = lazy(() => loadBannedPage().then((module) => ({ default: module.BannedPage })));
const PendingPage = lazy(() => loadPendingPage().then((module) => ({ default: module.PendingPage })));
const RejectedPage = lazy(() => loadRejectedPage().then((module) => ({ default: module.RejectedPage })));
const ProviderCallbackPage = lazy(() => import("@/pages/auth/ProviderCallbackPage").then((module) => ({ default: module.ProviderCallbackPage })));

const DashboardPage = lazy(() => loadDashboardPage().then((module) => ({ default: module.DashboardPage })));
const RequestsPage = lazy(() => loadRequestsPage().then((module) => ({ default: module.RequestsPage })));
const NewRequestPage = lazy(() => loadNewRequestPage().then((module) => ({ default: module.NewRequestPage })));
const RequestDetailPage = lazy(() => loadRequestDetailPage().then((module) => ({ default: module.RequestDetailPage })));
const SitesPage = lazy(() => loadSitesPage().then((module) => ({ default: module.SitesPage })));
const SiteEditorPage = lazy(() => loadSiteEditorPage().then((module) => ({ default: module.SiteEditorPage })));
const SiteEditsPage = lazy(() => loadSiteEditsPage().then((module) => ({ default: module.SiteEditsPage })));
const SiteRevisionsIndexPage = lazy(() => loadSiteRevisionsIndexPage().then((module) => ({ default: module.SiteRevisionsIndexPage })));
const ClientsPage = lazy(() => loadClientsPage().then((module) => ({ default: module.ClientsPage })));
const PaymentsPage = lazy(() => loadPaymentsPage().then((module) => ({ default: module.PaymentsPage })));
const FeedbackPage = lazy(() => loadFeedbackPage().then((module) => ({ default: module.FeedbackPage })));
const FeedbackDetailPage = lazy(() => loadFeedbackDetailPage().then((module) => ({ default: module.FeedbackDetailPage })));
const SupportTicketsPage = lazy(() => import("@/features/support/SupportPages").then((module) => ({ default: module.SupportTicketsPage })));
const TicketDetailPage = lazy(() => import("@/features/support/SupportPages").then((module) => ({ default: module.TicketDetailPage })));
const SupportToolsPage = lazy(() => import("@/features/support/SupportPages").then((module) => ({ default: module.SupportToolsPage })));
const SupportWorkspaceRoute = lazy(() => import("@/features/support/SupportPages").then((module) => ({ default: module.SupportWorkspaceRoute })));
const LegacySupportTicketsPage = lazy(() => loadLegacySupportTicketsPage().then((module) => ({ default: module.SupportTicketsPage })));
const LegacyTicketDetailPage = lazy(() => loadLegacyTicketDetailPage().then((module) => ({ default: module.TicketDetailPage })));
const ProfilePage = lazy(() => loadProfilePage().then((module) => ({ default: module.NewProfilePage })));
const NotificationsPage = lazy(() => loadNotificationsPage().then((module) => ({ default: module.NotificationsPage })));
const LeadsPage = lazy(() => loadLeadsPage().then((module) => ({ default: module.LeadsPage })));
const LeadsEscalationsPage = lazy(() => loadLeadsEscalationsPage().then((module) => ({ default: module.LeadsEscalationsPage })));
const WenlixAnalyticsPage = lazy(() => loadWenlixAnalyticsPage().then((module) => ({ default: module.WenlixAnalyticsPage })));

const AdminInvitesPage = lazy(() => loadAdminInvitesPage().then((module) => ({ default: module.AdminInvitesPage })));
const AdminPendingPage = lazy(() => loadAdminPendingPage().then((module) => ({ default: module.AdminPendingPage })));
const AdminPremiumApprovalPage = lazy(() => loadAdminPremiumApprovalPage().then((module) => ({ default: module.AdminPremiumApprovalPage })));
const AdminPremiumProductionPage = lazy(() => loadAdminPremiumProductionPage().then((module) => ({ default: module.AdminPremiumProductionPage })));
const AdminManagersPage = lazy(() => loadAdminManagersPage().then((module) => ({ default: module.AdminManagersPage })));
const AdminSupervisorsPage = lazy(() => loadAdminSupervisorsPage().then((module) => ({ default: module.AdminSupervisorsPage })));
const AdminDirectorsPage = lazy(() => loadAdminDirectorsPage().then((module) => ({ default: module.AdminDirectorsPage })));
const AdminDirectDeployPage = lazy(() => loadAdminDirectDeployPage().then((module) => ({ default: module.AdminDirectDeployPage })));
const AdminUsersPage = lazy(() => loadAdminUsersPage().then((module) => ({ default: module.AdminUsersPage })));
const AdminUsersKanbanPage = lazy(() => loadAdminUsersKanbanPage().then((module) => ({ default: module.AdminUsersKanbanPage })));
const AdminGroupsPage = lazy(() => loadAdminGroupsPage().then((module) => ({ default: module.AdminGroupsPageRedesign })));
const SiteDeletionRequestsPage = lazy(() => loadSiteDeletionRequestsPage().then((module) => ({ default: module.SiteDeletionRequestsPage })));
const AdminAppealsPage = lazy(() => loadAdminAppealsPage().then((module) => ({ default: module.AdminAppealsPage })));
const AdminAppealDetailPage = lazy(() => loadAdminAppealDetailPage().then((module) => ({ default: module.AdminAppealDetailPage })));
const AdminBroadcastPage = lazy(() => loadAdminBroadcastPage().then((module) => ({ default: module.AdminBroadcastPage })));
const AdminBillingPage = lazy(() => loadAdminBillingPage().then((module) => ({ default: module.AdminBillingPage })));
const DashboardLayout = lazy(() => import("@/components/layout/DashboardLayout").then((module) => ({ default: module.DashboardLayout })));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout").then((module) => ({ default: module.AdminLayout })));

// Premium loading screen
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6">
        <BrandMark className="h-16 w-16" glow glowClassName="animate-pulse bg-blue-500/35 blur-2xl" />
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
          <span className="text-muted-foreground text-sm font-medium">Загрузка...</span>
        </div>
      </div>
    </div>
  );
}

// Check if tokens exist
function hasTokens() {
  return isDemoSession() || hasValidTokens();
}

function useProviderSessionRestore(returnTo: string, enabled = true): boolean {
  const { authSource, clearUser, isAuthenticated, isHydrated, user } = useAuthStore();
  const expectedAccountUuid = user?.account_uuid ?? user?.id ?? null;
  const started = useRef(false);
  const action = isHydrated && enabled
    ? providerSessionRestoreAction({
        authSource,
        isAuthenticated,
        hasTokens: hasTokens(),
        providerEnabled: isProviderAuthEnabled(),
        restorePending: isProviderSessionRestorePending(),
        accountUuid: expectedAccountUuid,
      })
    : "none";

  useEffect(() => {
    if (action === "clear") {
      clearUser();
      return;
    }
    if (action !== "restore" || started.current) return;
    started.current = true;
    void beginProviderSessionRestore(returnTo, expectedAccountUuid!).catch(() => {
      clearUser();
    });
  }, [action, clearUser, expectedAccountUuid, returnTo]);

  return action !== "none";
}

function ProviderLogoutSync({ children }: { children: React.ReactNode }) {
  const clearUser = useAuthStore((state) => state.clearUser);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (!isProviderLogoutEpochStorageKey(event.key)) return;
      void revokeActiveProviderSessionAfterPeerLogout().catch(() => undefined);
      queryClient.clear();
      clearUser();
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clearUser]);

  return <>{children}</>;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isHydrated, user } = useAuthStore();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const restoringProviderSession = useProviderSessionRestore(
    `${location.pathname}${location.search}${location.hash}`,
  );

  useEffect(() => {
    // Small delay to ensure hydration is complete
    const timer = setTimeout(() => setChecked(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Wait for hydration and check
  if (!isHydrated || !checked || restoringProviderSession) {
    return <LoadingScreen />;
  }

  // Must have both store state AND tokens
  if (!isAuthenticated || !hasTokens()) {
    const returnTo = /^\/support(?:\/|$)/.test(location.pathname) ? `?returnTo=${encodeURIComponent(location.pathname + location.search)}` : "";
    return <Navigate to={`/login${returnTo}`} replace />;
  }

  if (user?.support_only && supportRouteSource(location.pathname) !== "native") return <Navigate to="/support" replace />;

  return <>{children}</>;
}

function RoleRoute({
  children,
  allow,
  redirectTo = "/dashboard",
}: {
  children: React.ReactNode;
  allow: UserRole[];
  redirectTo?: string;
}) {
  const { user, isHydrated } = useAuthStore();

  if (!isHydrated) {
    return <LoadingScreen />;
  }

  if (!user || !allow.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

// Auth route wrapper (redirects to dashboard if already logged in)
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isHydrated } = useAuthStore();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const addingProviderAccount =
    isProviderAuthRequested() && new URLSearchParams(location.search).get("addAccount") === "1";
  const restoringProviderSession = useProviderSessionRestore("/dashboard", !addingProviderAccount);

  useEffect(() => {
    const timer = setTimeout(() => setChecked(true), 50);
    return () => clearTimeout(timer);
  }, []);

  if (!isHydrated || !checked || restoringProviderSession) {
    return <LoadingScreen />;
  }

  // If authenticated with valid tokens, redirect to dashboard
  if (isAuthenticated && hasTokens() && !addingProviderAccount) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Invite route wrapper - ALWAYS clears tokens and shows registration
// This ensures invite links always lead to registration, never auto-login
function InviteRoute({ children }: { children: React.ReactNode }) {
  const { logout, isHydrated } = useAuthStore();

  // Clear tokens synchronously before first render via lazy initializer
  // This prevents auto-login with old/deleted account tokens
  const [cleared] = useState(() => {
    if (hasTokens()) {
      if (!isDemoSession()) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      }
      logout();
    }
    return true;
  });

  if (!isHydrated || !cleared) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useThemeStore();

  useEffect(() => {
    // Используем функцию applyTheme из store для консистентности
    const root = document.documentElement;
    root.classList.add("theme-transitioning");
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    void root.offsetHeight; // Принудительное обновление
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove("theme-transitioning");
      });
    });
  }, [theme]);

  return <>{children}</>;
}

export default function App() {
  const providerAuthRequested = isProviderAuthRequested();

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ThemeProvider>
          <ProviderLogoutSync>
            <BrowserRouter>
            <Suspense fallback={<LoadingScreen />}>
            <Routes>
            {/* Auth routes */}
            <Route
              path="/login"
              element={
                <AuthRoute>
                  <LoginPage />
                </AuthRoute>
              }
            />
            <Route path="/auth/callback" element={<ProviderCallbackPage />} />
            <Route
              path="/register"
              element={
                providerAuthRequested ? (
                  <Navigate to="/login" replace />
                ) : (
                  <AuthRoute>
                    <RegisterPage />
                  </AuthRoute>
                )
              }
            />
            <Route
              path="/register/:code"
              element={
                providerAuthRequested ? (
                  <Navigate to="/login" replace />
                ) : (
                  <InviteRoute>
                    <InviteRegisterPage />
                  </InviteRoute>
                )
              }
            />
            <Route
              path="/forgot-password"
              element={
                providerAuthRequested ? (
                  <Navigate to="/login" replace />
                ) : (
                  <AuthRoute>
                    <ForgotPasswordPage />
                  </AuthRoute>
                )
              }
            />
            <Route
              path="/banned"
              element={<BannedPage />}
            />
            <Route
              path="/pending"
              element={<PendingPage />}
            />
            <Route
              path="/rejected"
              element={<RejectedPage />}
            />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* Requests */}
              <Route path="requests" element={<RequestsPage />} />
              <Route path="requests/new" element={<NewRequestPage />} />
              <Route path="requests/:id" element={<RequestDetailPage />} />
              <Route path="archive" element={<Navigate to="/requests" replace />} />

              {/* Sites */}
              <Route path="sites" element={<SitesPage />} />
              <Route path="sites/:id" element={<SiteEditorPage />} />
              <Route path="sites/:id/edits" element={<SiteEditsPage />} />
              <Route path="sites/:id/revisions" element={<SiteEditsPage />} />

              {/* Clients */}
              <Route path="clients" element={<ClientsPage />} />

              {/* Site revisions */}
              <Route path="revisions" element={<SiteRevisionsIndexPage />} />
              <Route path="revisions/:id" element={<SiteEditsPage />} />
              <Route path="revisions/new" element={<Navigate to="/revisions" replace />} />

              {/* Payments */}
              <Route
                path="payments"
                element={
                  <RoleRoute allow={["manager", "supervisor", "director", "owner"]}>
                    <PaymentsPage />
                  </RoleRoute>
                }
              />
              <Route path="payments/new" element={<Navigate to="/payments" replace />} />

              {/* Feedback */}
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="feedback/:id" element={<FeedbackDetailPage />} />

              {/* Support Tickets */}
              <Route path="support" element={<SupportTicketsPage />} />
              <Route path="support/templates" element={<SupportToolsPage />} />
              <Route path="support/incidents" element={<SupportToolsPage />} />
              <Route path="support/reports" element={<SupportToolsPage />} />
              <Route path="support/sla" element={<SupportToolsPage />} />
              <Route path="support/studio" element={<LegacySupportTicketsPage />} />
              <Route path="support/tickets/:id/workspace" element={<SupportWorkspaceRoute />} />
              <Route path="support/tickets/:id" element={<TicketDetailPage />} />
              <Route path="support/:id/workspace" element={<SupportWorkspaceRoute />} />
              <Route path="support/:id" element={<LegacyTicketDetailPage />} />

              {/* Profile */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="settings" element={<ProfilePage />} />

              {/* Notifications */}
              <Route path="notifications" element={<NotificationsPage />} />

              {/* Leads (from lead-routing-service) */}
              <Route path="leads" element={<LeadsPage />} />
              <Route
                path="leads/escalations"
                element={
                  <RoleRoute allow={["supervisor", "director", "owner"]}>
                    <LeadsEscalationsPage />
                  </RoleRoute>
                }
              />
              <Route
                path="wenlix-analytics"
                element={
                  <RoleRoute allow={["supervisor", "director", "owner"]}>
                    <WenlixAnalyticsPage />
                  </RoleRoute>
                }
              />

              {/* Admin */}
              <Route
                path="admin"
                element={
                  <RoleRoute allow={["supervisor", "director", "owner"]}>
                    <AdminLayout />
                  </RoleRoute>
                }
              >
                <Route index element={<Navigate to={DEFAULT_ADMIN_PATH} replace />} />
                <Route path="dashboard" element={<Navigate to="/dashboard" replace />} />
                <Route path="invites" element={<AdminInvitesPage />} />
                <Route path="pending" element={<AdminPendingPage />} />
                <Route path="premium-approval" element={<AdminPremiumApprovalPage />} />
                <Route path="premium-production" element={<AdminPremiumProductionPage />} />
                <Route path="managers" element={<AdminManagersPage />} />
                <Route
                  path="supervisors"
                  element={
                    <RoleRoute allow={["director", "owner"]}>
                      <AdminSupervisorsPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="directors"
                  element={
                    <RoleRoute allow={["owner"]}>
                      <AdminDirectorsPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="direct-deploy"
                  element={
                    <RoleRoute allow={["owner"]}>
                      <AdminDirectDeployPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <RoleRoute allow={["director", "owner"]}>
                      <AdminUsersPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="users-kanban"
                  element={
                    <RoleRoute allow={["director", "owner"]}>
                      <AdminUsersKanbanPage />
                    </RoleRoute>
                  }
                />
                <Route path="groups" element={<AdminGroupsPage />} />
                <Route path="account-deletions" element={<RoleRoute allow={["supervisor", "director", "owner"]}><AccountDeletionRequestsPage /></RoleRoute>} />
                <Route path="requests" element={<Navigate to="/requests" replace />} />
                <Route path="sites" element={<Navigate to="/sites" replace />} />
                <Route path="clients" element={<Navigate to="/clients" replace />} />
                <Route path="appeals" element={<AdminAppealsPage />} />
                <Route path="appeals/:id" element={<AdminAppealDetailPage />} />
                <Route path="broadcast" element={<AdminBroadcastPage />} />
                <Route
                  path="billing"
                  element={
                    <RoleRoute allow={["supervisor", "director", "owner"]}>
                      <AdminBillingPage />
                    </RoleRoute>
                  }
                />
                <Route path="stats" element={<Navigate to="/dashboard" replace />} />
                <Route path="owner" element={<Navigate to="/dashboard" replace />} />
                <Route path="deletion-requests" element={<SiteDeletionRequestsPage />} />
              </Route>
            </Route>

            {/* Invite code short link - must be after all specific routes but before catch-all */}
            <Route
              path="/:code"
              element={
                providerAuthRequested ? (
                  <Navigate to="/login" replace />
                ) : (
                  <InviteRoute>
                    <InviteRedirectPage />
                  </InviteRoute>
                )
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
            </Suspense>
            </BrowserRouter>
          </ProviderLogoutSync>
      </ThemeProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
